<template>
  <div class="ng-green-data">
    <div class="top">
      <div class="page-header">
        <div class="title-row">
          <div class="title">{{ t('pageTitle') }}</div>
          <div v-if="isChangeLanguage" class="lang-switch">
            <el-select v-model="lang" size="mini" class="lang-select">
              <el-option label="EN" value="en" />
              <el-option label="中文" value="zh" />
            </el-select>
          </div>
        </div>
      </div>

      <div class="toolbar">
        <DateTimeRangePicker
          v-model="dateRange"
          picker-type="daterange"
          size="mini"
          :clearable="false"
          class="picker"
          :i18n="pickerI18n"
        />
        <div class="quick">
          <el-button
            v-for="item in quickRangeOptions"
            :key="item.key"
            size="mini"
            :type="currentDateRangeKey === item.key ? 'primary' : 'default'"
            @click="applyQuickRange(item.key)"
          >
            {{ item.label }}
          </el-button>
        </div>
      </div>
    </div>

    <div class="content">
      <div class="cards" v-loading="loading">
        <div class="card">
          <div class="card-title">{{ t('overviewTitle') }}</div>
          <div class="metrics metrics--1">
            <div class="metric">
              <div class="label">{{ t('totalProfit') }}</div>
              <div class="value">{{ formatNgn(stats.overview.totalProfit) }}</div>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-title">{{ t('usersTitle') }}</div>
          <div class="metrics metrics--4">
            <div class="metric">
              <div class="label">{{ t('registrations') }}</div>
              <div class="value">{{ formatInt(stats.user.registerCount) }}</div>
            </div>
            <div class="metric">
              <div class="label">{{ t('firstDepositors') }}</div>
              <div class="value">{{ formatInt(stats.user.firstDepositUsers) }}</div>
            </div>
            <div class="metric">
              <div class="label">{{ t('totalPlayerBalance') }}</div>
              <div class="value">{{ formatNgn(stats.user.totalBalance) }}</div>
            </div>
            <div class="metric">
              <div class="label">{{ t('newUsers') }}</div>
              <div class="value">{{ formatInt(stats.user.newUsers) }}</div>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-title">{{ t('depositsTitle') }}</div>
          <div class="metrics metrics--4">
            <div class="metric">
              <div class="label">{{ t('totalDepositAmount') }}</div>
              <div class="value">{{ formatNgn(stats.deposit.totalAmount) }}</div>
            </div>
            <div class="metric">
              <div class="label">{{ t('depositUsers') }}</div>
              <div class="value">{{ formatInt(stats.deposit.users) }}</div>
            </div>
            <div class="metric">
              <div class="label">{{ t('firstDepositors') }}</div>
              <div class="value">{{ formatInt(stats.deposit.firstDepositUsers) }}</div>
            </div>
            <div class="metric">
              <div class="label">{{ t('depositFees') }}</div>
              <div class="value">{{ formatNgn(stats.deposit.fee) }}</div>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-title">{{ t('withdrawalsTitle') }}</div>
          <div class="metrics metrics--2">
            <div class="metric">
              <div class="label">{{ t('totalWithdrawalAmount') }}</div>
              <div class="value">{{ formatNgn(stats.withdraw.totalAmount) }}</div>
            </div>
            <div class="metric">
              <div class="label">{{ t('withdrawalFees') }}</div>
              <div class="value">{{ formatNgn(stats.withdraw.fee) }}</div>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-title">{{ t('gamesTitle') }}</div>
          <div class="metrics metrics--2">
            <div class="metric">
              <div class="label">{{ t('gameWinLoss') }}</div>
              <div class="value" :class="signClass(stats.game.winLose)">
                {{ formatSignedNgn(stats.game.winLose) }}
              </div>
            </div>
            <div class="metric">
              <div class="label">{{ t('betAmount') }}</div>
              <div class="value">{{ formatNgn(stats.game.betAmount) }}</div>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-title">{{ t('promotionsTitle') }}</div>
          <div class="metrics metrics--3">
            <div class="metric">
              <div class="label">{{ t('manualCredit') }}</div>
              <div class="value">{{ formatNgn(stats.activity.manualUpScore) }}</div>
            </div>
            <div class="metric">
              <div class="label">{{ t('depositBonus') }}</div>
              <div class="value">{{ formatNgn(stats.activity.depositBonus) }}</div>
            </div>
            <div class="metric">
              <div class="label">{{ t('platformBonus') }}</div>
              <div class="value">{{ formatNgn(stats.activity.platformBonus) }}</div>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-title">{{ t('agentCommissionTitle') }}</div>
          <div class="metrics metrics--2">
            <div class="metric">
              <div class="label">{{ t('totalCommission') }}</div>
              <div class="value">{{ formatNgn(stats.agent.totalCommission) }}</div>
            </div>
            <div class="metric">
              <div class="label">{{ t('withdrawn') }}</div>
              <div class="value">{{ formatNgn(stats.agent.withdrawn) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import DateTimeRangePicker from '@/components/DateTimeRangePicker'
import { DISPLAY_TIMEZONE, getCalendarPartsInTimeZone } from '@/utils/displayTimezone'
import { toUnixSeconds } from '@/utils/time'
import { getPublicOverview } from '@/api/public/overview'
import MD5 from 'crypto-js/md5'
import ElementLocale from 'element-ui/lib/locale'

// const isDev = process.env.NODE_ENV === 'development'
const isChangeLanguage = process.env.VUE_APP_CHANGE_LANGUAGE === 'true'
const lang = ref('en')

const dict = {
  en: {
    pageTitle: 'Data Analytics',
    overviewTitle: 'Overview',
    usersTitle: 'Users',
    depositsTitle: 'Deposits',
    withdrawalsTitle: 'Withdrawals',
    gamesTitle: 'Games',
    promotionsTitle: 'Promotions',
    agentCommissionTitle: 'Agent Commission',
    totalProfit: 'Total Profit',
    registrations: 'Registrations',
    firstDepositors: 'First Depositors',
    totalPlayerBalance: 'Total Player Balance',
    newUsers: 'New Users',
    totalDepositAmount: 'Total Deposit Amount',
    depositUsers: 'Depositors',
    depositFees: 'Deposit Fees',
    totalWithdrawalAmount: 'Total Withdrawal Amount',
    withdrawalFees: 'Withdrawal Fees',
    gameWinLoss: 'Game Win/Loss',
    betAmount: 'Bet Amount',
    manualCredit: 'Manual Credit',
    depositBonus: 'Deposit Bonus',
    platformBonus: 'Platform Bonus',
    totalCommission: 'Total Commission',
    withdrawn: 'Withdrawn',
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This Week',
    lastWeek: 'Last Week',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
    picker: {
      rangeSeparator: 'To',
      startPlaceholder: 'Start date',
      endPlaceholder: 'End date',
      today: 'Today',
      yesterday: 'Yesterday',
      thisWeek: 'This week',
      lastWeek: 'Last week',
      thisMonth: 'This month',
      lastMonth: 'Last month',
    },
  },
  zh: {
    pageTitle: '数据分析模块',
    overviewTitle: '数据概况',
    usersTitle: '用户数据',
    depositsTitle: '充值数据',
    withdrawalsTitle: '提现数据',
    gamesTitle: '游戏数据',
    promotionsTitle: '活动数据',
    agentCommissionTitle: '代理佣金',
    totalProfit: '总利润',
    registrations: '注册人数',
    firstDepositors: '首充人数',
    totalPlayerBalance: '玩家总余额',
    newUsers: '新增用户',
    totalDepositAmount: '总充值金额',
    depositUsers: '充值人数',
    depositFees: '充值手续费',
    totalWithdrawalAmount: '总提现金额',
    withdrawalFees: '提现手续费',
    gameWinLoss: '游戏输赢',
    betAmount: '投注额',
    manualCredit: '手动上分',
    depositBonus: '充值送',
    platformBonus: '平台活动送',
    totalCommission: '佣金总额',
    withdrawn: '代理已领取',
    today: '今日',
    yesterday: '昨日',
    thisWeek: '本周',
    lastWeek: '上周',
    thisMonth: '本月',
    lastMonth: '上月',
    picker: {
      rangeSeparator: '至',
      startPlaceholder: '开始日期',
      endPlaceholder: '结束日期',
      today: '今日',
      yesterday: '昨日',
      thisWeek: '本周',
      lastWeek: '上周',
      thisMonth: '本月',
      lastMonth: '上月',
    },
  },
}

function t(key) {
  const pack = dict[lang.value] || dict.en
  return pack[key] != null ? pack[key] : dict.en[key] || key
}

const quickRangeOptions = computed(() => [
  { key: 'today', label: t('today') },
  { key: 'yesterday', label: t('yesterday') },
  { key: 'thisWeek', label: t('thisWeek') },
  { key: 'lastWeek', label: t('lastWeek') },
  { key: 'thisMonth', label: t('thisMonth') },
  { key: 'lastMonth', label: t('lastMonth') },
])

function pad2(n) {
  return n < 10 ? `0${n}` : String(n)
}

function formatYMD({ y, m, d }) {
  return `${y}-${pad2(m)}-${pad2(d)}`
}

function addDaysYMD({ y, m, d, a }, deltaDays) {
  const base = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)))
  base.setUTCDate(base.getUTCDate() + deltaDays)
  const next = { y: base.getUTCFullYear(), m: base.getUTCMonth() + 1, d: base.getUTCDate() }
  if (a != null) {
    next.a = (Number(a) + deltaDays) % 7
    if (next.a < 0) next.a += 7
  }
  return next
}

function lastDayOfMonthYMD({ y, m }) {
  const d = new Date(Date.UTC(Number(y), Number(m), 0))
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate() }
}

function getLagosTodayYMD() {
  const now = new Date()
  const p = getCalendarPartsInTimeZone(now, DISPLAY_TIMEZONE)
  return { y: p.y, m: p.m, d: p.d, a: p.a }
}

const dateRange = ref([formatYMD(getLagosTodayYMD()), formatYMD(getLagosTodayYMD())])
const currentDateRangeKey = ref('today')
const lastAppliedQuickKey = ref('today')

const pickerI18n = computed(() => {
  const pack = dict[lang.value] || dict.en
  return pack.picker || dict.en.picker
})

async function applyElementUiLocale(nextLang) {
  // element-ui 的 el-date-picker 月份/星期等文案依赖全局 locale
  // 这里仅在本页开启多语时同步切换
  if (!isChangeLanguage) return
  try {
    if (nextLang === 'zh') {
      const m = await import('element-ui/lib/locale/lang/zh-CN')
      ElementLocale.use(m.default || m)
    } else {
      const m = await import('element-ui/lib/locale/lang/en')
      ElementLocale.use(m.default || m)
    }
  } catch (e) {
    // 忽略：不阻塞页面
  }
}

watch(
  () => lang.value,
  v => {
    applyElementUiLocale(v)
  },
  { immediate: true }
)

function applyQuickRange(key) {
  const today = getLagosTodayYMD()
  let start = today
  let end = today

  if (key === 'today') {
    start = today
    end = today
  } else if (key === 'yesterday') {
    start = addDaysYMD(today, -1)
    end = addDaysYMD(today, -1)
  } else if (key === 'thisWeek') {
    // a: 0=Sun ... 6=Sat
    const weekday = Number(today.a ?? 0)
    const deltaToMonday = (weekday + 6) % 7
    start = addDaysYMD(today, -deltaToMonday)
    end = today
  } else if (key === 'lastWeek') {
    const weekday = Number(today.a ?? 0)
    const deltaToMonday = (weekday + 6) % 7
    const thisMonday = addDaysYMD(today, -deltaToMonday)
    start = addDaysYMD(thisMonday, -7)
    end = addDaysYMD(thisMonday, -1)
  } else if (key === 'thisMonth') {
    start = { y: today.y, m: today.m, d: 1 }
    end = today
  } else if (key === 'lastMonth') {
    const prevMonth = addDaysYMD({ y: today.y, m: today.m, d: 1 }, -1)
    start = { y: prevMonth.y, m: prevMonth.m, d: 1 }
    end = lastDayOfMonthYMD(prevMonth)
  }

  lastAppliedQuickKey.value = key
  currentDateRangeKey.value = key
  dateRange.value = [formatYMD(start), formatYMD(end)]
}

watch(
  () => dateRange.value,
  () => {
    // 只要用户手动改了范围，就视为 custom（除非刚刚点了快捷范围）
    if (currentDateRangeKey.value === lastAppliedQuickKey.value) return
    currentDateRangeKey.value = 'custom'
  },
  { deep: true }
)

function emptyStats() {
  return {
    overview: { totalProfit: null },
    user: { registerCount: null, firstDepositUsers: null, totalBalance: null, newUsers: null },
    deposit: { totalAmount: null, users: null, firstDepositUsers: null, fee: null },
    withdraw: { totalAmount: null, fee: null },
    game: { winLose: null, betAmount: null },
    activity: { manualUpScore: null, depositBonus: null, platformBonus: null },
    agent: { totalCommission: null, withdrawn: null },
  }
}

const loading = ref(false)
const apiStats = ref(emptyStats())

function buildTimeParams() {
  const r = dateRange.value
  if (!r || r.length < 2 || !r[0] || !r[1]) return { startAt: 0, endAt: 0 }
  const startAt = toUnixSeconds(`${r[0]} 00:00:00`) || 0
  const endAt = toUnixSeconds(`${r[1]} 23:59:59`) || 0
  return { startAt, endAt }
}

function signFor(startAt, endAt) {
  const raw = `${startAt}|${endAt}|167u!a9jJWou@0v~`
  console.log(raw, 'raw')

  return MD5(raw).toString()
}

function mapApiToStats(raw) {
  const d = raw || {}
  return {
    overview: { totalProfit: d.profit_amount },
    user: {
      registerCount: d.player_count,
      firstDepositUsers: d.successFirstPayinCount,
      totalBalance: d.total_player_change_gold,
      newUsers: d.register_unique_num,
    },
    deposit: {
      totalAmount: d.success_payin_amount,
      users: d.success_payin_unique_num,
      firstDepositUsers: d.successFirstPayinCount,
      fee: d.success_payin_fee,
    },
    withdraw: {
      totalAmount: d.success_payout_amount,
      fee: d.success_payout_fee,
    },
    game: {
      winLose: d.win_loss_amount,
      betAmount: d.bet_amount,
    },
    activity: {
      manualUpScore: d.player_gold_admin_add,
      depositBonus: d.success_payin_bonus,
      platformBonus: d.activity_gold,
    },
    agent: {
      totalCommission: d.agent_back_gold,
      withdrawn: d.agent_to_gold,
    },
  }
}

async function loadOverview() {
  const { startAt, endAt } = buildTimeParams()
  loading.value = true
  try {
    const res = await getPublicOverview({
      start_at: startAt,
      end_at: endAt,
      sign: signFor(startAt, endAt),
    })
    apiStats.value = mapApiToStats(res?.data)
  } catch (e) {
    // request.js 已统一 toast，这里不重复提示
  } finally {
    loading.value = false
  }
}

watch(
  () => dateRange.value,
  () => loadOverview(),
  { deep: true, immediate: true }
)

const stats = computed(() => apiStats.value || emptyStats())

function formatInt(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '--'
  return new Intl.NumberFormat('en-NG').format(n)
}

function formatNgn(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '--'
  // 接口金额单位：分 → NGN
  return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(n / 100)
}

function formatSignedNgn(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '--'
  const abs = Math.abs(n)
  const text = formatNgn(abs)
  return n > 0 ? `+${text}` : n < 0 ? `-${text}` : text
}

function signClass(v) {
  const n = Number(v)
  if (!Number.isFinite(n) || n === 0) return ''
  return n > 0 ? 'pos' : 'neg'
}
</script>

<style scoped>
.ng-green-data {
  height: 100vh;
  background: #0f172a;
  color: #e5e7eb;
  padding: 12px 12px 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.top {
  flex: none;
}
.content {
  flex: 1;
  min-height: 0;
}

.page-header {
  margin-bottom: 8px;
}
.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.title {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.2px;
}
.lang-switch {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
}
.lang-select {
  width: 90px;
}
.subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(229, 231, 235, 0.7);
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 10px;
  gap: 10px;
  flex-wrap: wrap;
}
.picker {
  width: 300px;
}
.quick {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.quick :deep(.el-button) {
  border: 1px solid rgba(148, 163, 184, 0.25);
}
.quick :deep(.el-button--default) {
  background: rgba(15, 23, 42, 0.3);
  color: #e5e7eb;
}
.quick :deep(.el-button--primary) {
  background: rgba(59, 130, 246, 0.95);
  border-color: rgba(59, 130, 246, 0.95);
}

.cards {
  height: 100%;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  grid-auto-rows: 1fr;
  align-items: stretch;
}
.card {
  background: rgba(2, 6, 23, 0.55);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 10px;
  padding: 10px;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.card-title {
  font-size: 13px;
  font-weight: 800;
  margin-bottom: 8px;
}

.metrics {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  align-content: start;
}
.metrics--1 {
  grid-template-columns: 1fr;
  grid-auto-rows: 1fr;
}
.metrics--2 {
  grid-template-columns: 1fr;
  grid-template-rows: repeat(2, minmax(0, 1fr));
  grid-auto-rows: 1fr;
}
.metrics--3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-auto-rows: 1fr;
}
.metrics--4 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: 1fr;
}
.metric {
  background: rgba(2, 6, 23, 0.55);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 10px;
  padding: 10px 10px 8px;
  min-height: 0;
}
.label {
  font-size: 12px;
  color: rgba(229, 231, 235, 0.72);
}
.value {
  margin-top: 4px;
  font-size: 15px;
  font-weight: 800;
  color: #f8fafc;
  word-break: break-word;
}
.metrics--1 .value {
  font-size: 36px;
  line-height: 1.15;
}
.metrics--2 .value {
  font-size: 28px;
  line-height: 1.15;
}
.metrics--3 .value,
.metrics--4 .value {
  font-size: 24px;
  line-height: 1.15;
}
.value.pos {
  color: rgba(34, 197, 94, 0.95);
}
.value.neg {
  color: rgba(239, 68, 68, 0.95);
}

@media (max-width: 1100px) {
  .cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 520px) {
  .cards {
    grid-template-columns: 1fr;
  }
  .metrics--2,
  .metrics--3,
  .metrics--4 {
    grid-template-columns: 1fr;
  }
  .title {
    font-size: 18px;
  }
  .picker {
    width: 100%;
  }
}
</style>
